import { type TextareaHTMLAttributes, forwardRef } from "react";
import { cn } from "../../lib/utils";

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * The DS multi-line input.
 *
 * The app had five raw `<textarea>` elements and no component, so the styling was
 * copied by hand each time. This is the one from the profile editor, which is the
 * version the design system settled on — lifted rather than reinvented.
 *
 * `resize-none` on purpose: the sheet this lives in is height-capped and scrolls,
 * so a user-resizable box fights the container.
 *
 * `shrink-0` is load-bearing, not tidying. In a flex column that overflows — which
 * is exactly what the annotation sheet is — flex hands out the missing space by
 * shrinking its children, and `min-height: auto` only resolves to the content
 * height when the item's own `overflow` is `visible`. A `<textarea>` defaults to
 * `overflow: auto`, so its automatic minimum is ZERO: it is the one child that can
 * be crushed to nothing, and it therefore absorbs the whole shrink while the
 * sibling divs keep their height. The box then renders as a single squashed line
 * with `rows` silently overridden. `shrink-0` keeps the height `rows` asks for and
 * lets the parent scroll instead, which is what it is already set up to do.
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, rows = 4, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(
          // Borrowed from `Input`, class for class, rather than re-invented: the
          // two sit one above the other in the same form, and a focus ring that
          // differs between them reads as a bug in the form rather than as two
          // components. `Input` marks focus by DARKENING THE BORDER, so this does
          // too — the previous grey box-shadow was lifted from the profile editor,
          // which is not this component's sibling.
          "w-full shrink-0 resize-none rounded-xl border border-solid px-4 py-3",
          "border-[rgba(0,0,0,0.25)] bg-[rgba(255,255,255,0.85)]",
          "shadow-[0px_2px_8px_rgba(0,0,0,0.08)]",
          "transition-colors duration-150 ease-out",
          "focus:border-[rgba(0,0,0,0.4)] focus:outline-none",
          "font-dm-sans text-ui-body text-ds-black placeholder:text-ds-slate-400",
          className
        )}
        {...props}
      />
    );
  }
);

export { Textarea, type TextareaProps };
